import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  VStack, 
  Heading, 
  Text,
  SimpleGrid,
  HStack,
  Icon,
  Flex,
  useColorModeValue,
  Circle,
  Progress,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Shield, 
  MessageCircle, 
  Database, 
  Lock, 
  Server, 
  ChevronRight, 
  CheckCircle, 
  Zap, 
  Heart, 
  BarChart, 
  Users, 
  Sparkles,
  Lightbulb,
  Clock,
  BookOpen,
} from 'lucide-react';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import { useAudio } from '../../context/AudioContext';

// Import the UnifiedMobilePlatform component
import UnifiedMobilePlatform from '../ui-samples/UnifiedMobilePlatform';

// Methodist color palette
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';
const methodistGreen = '#38A169';
const methodistPurple = '#805AD5';

/**
 * Presenter's Guide for AICompanionSlide:
 * 
 * 1. Introduction (30 seconds):
 *    - Introduce the AI Wellness Companion as a key feature of the SentientIQ platform
 *    - Emphasize that this is a personalized wellness coach for healthcare professionals
 *    - Highlight how it integrates with the Adaptability Quotient domain
 * 
 * 2. Key Features Section (45 seconds):
 *    - Point out the four key features of the AI Companion:
 *      a. Personalized Wellness Coaching: Tailored to individual needs
 *      b. Evidence-Based Guidance: Powered by RAG technology
 *      c. Stress Management: Practical techniques for resilience
 *      d. Practice Efficiency: Strategies to optimize workflows
 * 
 * 3. Technology & Security (30 seconds):
 *    - Explain the advanced technology behind the AI Companion
 *    - Emphasize the security and privacy measures in place
 * 
 * 4. Mobile Experience Demo (1 minute):
 *    - Show how the AI Companion works within the mobile interface
 *    - Demonstrate the chat functionality and wellness suggestions
 *    - Highlight how it provides sources for evidence-based recommendations
 * 
 * 5. Conclusion (15 seconds):
 *    - Summarize how the AI Companion supports clinician wellness
 *    - Transition to the next slide
 */

// Full narration script for presentation - balanced length with technical details
const narrationScript = `The AI Wellness Companion serves as a personalized digital coach designed specifically for healthcare professionals at Methodist.

This intelligent assistant is powered by advanced Retrieval-Augmented Generation technology, or RAG, which combines large language models with a specialized knowledge base of wellness research, stress management techniques, and practice efficiency strategies.

Unlike traditional chatbots, the RAG architecture enables the AI Companion to provide evidence-based recommendations by retrieving relevant information from peer-reviewed sources and clinical best practices, ensuring accuracy and reliability.

The system employs robust security measures with end-to-end encryption and role-based access controls to maintain the highest standards of privacy and data protection.

Take a moment to interact with the JIMI Wellness Coach on your screen. You can ask about specific wellness strategies, stress management techniques, or ways to optimize your clinical workflows.

When you're ready to continue exploring the platform's features, simply advance to the next slide.`;

const AICompanionSlide = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');

  // Use the audio context
  const { playAudioForSlide } = useAudio();

  // Play audio when component mounts
  useEffect(() => {
    // Play audio with a slight delay to ensure DOM is ready
    const audioTimer = setTimeout(() => {
      console.log('Starting audio playback for AI Companion slide...');
      playAudioForSlide('ai-companion');
    }, 800);
    
    // Cleanup function
    return () => {
      clearTimeout(audioTimer);
      // Audio cleanup is handled by AudioContext
    };
  }, [playAudioForSlide]);

  // Features data
  const features = [
    {
      icon: Heart,
      title: "Personalized Wellness Coaching",
      description: "Tailored guidance based on individual wellness metrics and preferences",
      color: methodistGreen,
      impactMetric: "92% satisfaction",
      scalabilityInfo: "Personalized support at scale"
    },
    {
      icon: Database,
      title: "Evidence-Based Guidance",
      description: "Recommendations powered by the latest wellness research and best practices",
      color: methodistBlue,
      impactMetric: "85% accuracy",
      scalabilityInfo: "RAG technology integration"
    },
    {
      icon: Brain,
      title: "Stress Management",
      description: "Practical techniques to reduce stress and build resilience",
      color: methodistPurple,
      impactMetric: "78% stress reduction",
      scalabilityInfo: "Adaptive stress protocols"
    },
    {
      icon: Zap,
      title: "Practice Efficiency",
      description: "Strategies to optimize workflows and reduce administrative burden",
      color: methodistNavy,
      impactMetric: "68% time savings",
      scalabilityInfo: "Workflow optimization"
    }
  ];

  return (
    <MethodistSlide 
      showLogo={true}
      captionsText={narrationScript}
      isSpeaking={undefined}
      showCaptions={undefined}
    >
      <Grid templateColumns="2fr 1fr" gap={8} h="100%" py={6}>
        {/* Left side: Features and content */}
        <VStack align="stretch" spacing={8}>
          {/* Introduction */}
          <Box>
            <Heading size="md" color={methodistBlue} mb={3}>
              SentientIQ AI Wellness Companion
            </Heading>
            <Text fontSize="md" color={textColor}>
              The SentientIQ AI Wellness Companion provides personalized coaching and evidence-based guidance
              to support clinician wellness, stress management, and practice efficiency through natural conversation.
            </Text>
          </Box>

          {/* Key Features */}
          <Box>
            <Heading size="sm" mb={4}>Key Features</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {features.map((feature, index) => (
                <HStack
                  key={index}
                  spacing={4}
                  p={4}
                  bg={bgColor}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  shadow="sm"
                  as={motion.div}
                  whileHover={{ scale: 1.02 }}
                  transition={{ ease: "easeInOut" }}
                >
                  <Flex
                    align="center"
                    justify="center"
                    bg={`${feature.color}15`}
                    color={feature.color}
                    borderRadius="md"
                    p={2}
                    boxSize="40px"
                  >
                    <Icon as={feature.icon} boxSize={5} />
                  </Flex>
                  <VStack align="start" spacing={1} flex={1}>
                    <Text fontWeight="medium" fontSize="sm">
                      {feature.title}
                    </Text>
                    <Text fontSize="xs" color={textColor}>
                      {feature.description}
                    </Text>
                    <HStack fontSize="xs" spacing={3} mt={1}>
                      <Text color={feature.color} fontWeight="medium">
                        {feature.impactMetric}
                      </Text>
                      <Text color="gray.500">
                        {feature.scalabilityInfo}
                      </Text>
                    </HStack>
                  </VStack>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>

          {/* Technology and Security Grid */}
          <SimpleGrid columns={2} spacing={8}>
            {/* Left Column: Technology */}
            <Box>
              <Heading size="sm" mb={4}>Technology</Heading>
              <VStack spacing={4}>
                {[
                  {
                    title: "Retrieval-Augmented Generation",
                    description: "Combines knowledge retrieval with generative AI for evidence-based guidance",
                    color: methodistBlue
                  },
                  {
                    title: "Natural Language Understanding",
                    description: "Advanced comprehension of wellness-related queries and clinical terminology",
                    color: methodistGreen
                  },
                  {
                    title: "Personalization Engine",
                    description: "Tailors responses to individual needs and wellness journey",
                    color: methodistPurple
                  }
                ].map((tech, index) => (
                  <Box
                    key={index}
                    p={4}
                    bg={bgColor}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                    shadow="sm"
                    w="full"
                    as={motion.div}
                    whileHover={{ scale: 1.02 }}
                  >
                    <HStack spacing={4} align="start">
                      <Circle size="24px" bg={`${tech.color}15`}>
                        <Text fontSize="xs" fontWeight="bold" color={tech.color}>
                          {index + 1}
                        </Text>
                      </Circle>
                      <VStack align="start" spacing={1}>
                        <Text
                          fontWeight="semibold"
                          fontSize="sm"
                          color={tech.color}
                        >
                          {tech.title}
                        </Text>
                        <Text fontSize="xs" color={textColor}>
                          {tech.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>

            {/* Right Column: Security and Benefits */}
            <VStack spacing={6}>
              {/* Security Features */}
              <Box w="full">
                <Heading size="sm" mb={4}>Security & Privacy</Heading>
                <VStack 
                  align="start" 
                  spacing={3} 
                  bg={bgColor} 
                  p={4} 
                  borderRadius="md" 
                  borderWidth="1px" 
                  borderColor={borderColor}
                  shadow="sm"
                >
                  {[
                    "End-to-end encryption for all conversations",
                    "Privacy-first design with user data controls",
                    "Compliance with healthcare security standards",
                    "No persistent storage of personal conversations",
                    "Secure cloud infrastructure with regular audits"
                  ].map((security, index) => (
                    <HStack key={index} spacing={3} w="full">
                      <Circle size="16px" bg={`${methodistBlue}15`}>
                        <Icon as={Shield} boxSize={3} color={methodistBlue} />
                      </Circle>
                      <Text fontSize="xs" color={textColor}>
                        {security}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              {/* Key Metrics */}
              <Box w="full">
                <Heading size="sm" mb={4}>Impact Metrics</Heading>
                <HStack spacing={3}>
                  {[
                    {
                      label: "User Satisfaction",
                      value: "92",
                      goal: "100",
                      color: methodistBlue,
                      icon: Heart
                    },
                    {
                      label: "Wellness Engagement",
                      value: "85",
                      goal: "100",
                      color: methodistGreen,
                      icon: Users
                    },
                    {
                      label: "Knowledge Accuracy",
                      value: "95",
                      goal: "100",
                      color: methodistPurple,
                      icon: Database
                    }
                  ].map((metric, index) => (
                    <Box
                      key={index}
                      p={3}
                      bg={bgColor}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={borderColor}
                      shadow="sm"
                      flex={1}
                    >
                      <VStack spacing={2}>
                        <Icon as={metric.icon} color={metric.color} boxSize={4} />
                        <Text fontSize="xs" color={textColor} noOfLines={1}>
                          {metric.label}
                        </Text>
                        <HStack spacing={1}>
                          <Text fontSize="sm" fontWeight="bold" color={metric.color}>
                            {metric.value}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            /{metric.goal}
                          </Text>
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                </HStack>
              </Box>
            </VStack>
          </SimpleGrid>
        </VStack>

        {/* Right side: Mobile UI Demo */}
        <VStack align="start" spacing={4}>
          <Box>
            <Heading size="sm" mb={2}>Mobile Experience</Heading>
            <Text fontSize="sm" color={textColor}>
              Experience the SentientIQ AI Wellness Companion in action. Our intuitive chat interface provides personalized
              wellness coaching, evidence-based recommendations, and stress management techniques—all optimized for busy healthcare professionals.
            </Text>
          </Box>
          <Box 
            bg={bgColor}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            shadow="sm"
            p={4}
            w="full"
          >
            <UnifiedMobilePlatform 
              activeDomain="AQ"
              defaultTab="assistant"
              highlightedFeature="wellness"
            />
          </Box>
        </VStack>
      </Grid>
    </MethodistSlide>
  );
};

export default AICompanionSlide;
